<script>
import {computed, onMounted, ref, watch} from "vue";
import {useControlProgramStore} from "@/views/Notification/stores/ControlProgram";
import useSharedData from "@/use/SharedData";
import useValidation from "@/use/Validation";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import useSharedControlProgram from "@/views/Notification/use/SharedControlProgram";
import axios from "axios";

export default {
    name: "ChildFormModel",
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        category: String,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {dialog, loading, overlay, inputColor, backgroundColor, cardBackgroundColor} = useSharedData();
        const {mxRules} = useValidation();
        const {getBack} = useSharedMethod();
        const {$route, $refs} = useTools();
        const {simpleControlOperators, getDataSelectTag, getEnumList} = useSharedControlProgram();
        const controlProgramStore = useControlProgramStore();
        
        
        const controlProgram = ref({
            typeId: null,
            color: null,
            operator: "equal",
            size: 0,
            days: 0,
            booleanCheck: false,
            firstNumber: 0,
            secondNumber: 0,
            isCombine: false,
            isMiddle: true,
            dateCompareOption: 'FULL',
            category: props.category
        });
        
        const typeId = computed(() => controlProgram.value.typeId);
        
        const loadingType = ref(false);
        const type = ref(null);
        const types = ref([]);
        
        function findTypeById(id) {
            axios.get(`/controlTypes/${id}`).then(response => {
                type.value = response.data;
            });
        }
        
        watch(typeId, () => findTypeById(typeId.value));
        
        function findTypes() {
            loadingType.value = true;
            axios.get('/controlTypes').then(response => {
                types.value = response.data;
            }).finally(() => {
                loadingType.value = false;
            });
        }
        
        
        function findById() {
            loading.value = true;
            axios.get(`/controlPrograms/${$route.params.id}`).then(response => {
                controlProgram.value = response.data;
            }).finally(() => {
                loading.value = false;
            })
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                controlProgram.value.module = $route.params.module;
                axios.post('/controlPrograms', controlProgram.value).then(response => {
                    controlProgramStore.addControlProgram(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                })
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                controlProgram.value.module = $route.params.module;
                axios.put('/controlPrograms', controlProgram.value).then(response => {
                    controlProgramStore.updateControlProgram(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                })
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/controlPrograms/${$route.params.id}`).then(() => {
                controlProgramStore.deleteControlProgram(controlProgram.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            })
        }
        
        onMounted(() => {
            findTypes();
            if (!props.addAction) {
                findById();
            }
        });
        
        return {
            mxRules,
            controlProgramStore,
            readonly,
            dialog,
            loading,
            overlay,
            inputColor,
            backgroundColor,
            cardBackgroundColor,
            controlProgram,
            loadingType,
            type,
            types,
            simpleControlOperators,
            save,
            update,
            remove,
            getBack,
            getDataSelectTag,
            getEnumList,
        }
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        width="700"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="controlProgram.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('common.type')" required/>
                                    <w-enum-select
                                        v-model="controlProgram.typeId"
                                        :items="types"
                                        item-text="label"
                                        :loading="loadingType"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.definition')">
                            <v-row dense>
                                <v-col cols="5">
                                    <v-radio-group v-model="controlProgram.option" :readonly="readonly">
                                        <div v-if="type.fieldType !== 'STATIC'">
                                            <v-radio
                                                value="isNotNull"
                                                :label="$t('alarm.control_program_option_is_not_null')"
                                            />
                                            <v-radio
                                                value="isNull"
                                                :label="$t('alarm.control_program_option_is_null')"
                                            />
                                            <v-radio
                                                value="equal"
                                                :label="$t('alarm.control_program_option_equal')"
                                            />
                                            <v-radio
                                                value="notEqual"
                                                :label="$t('alarm.control_program_option_not_equal')"
                                            />
                                        </div>
                                        
                                        <div
                                            v-if="['DATE', 'DATETIME', 'TIME', 'TIMESTAMP'].includes(type.fieldType)">
                                            <v-radio
                                                value="dateBetween"
                                                :label="$t('alarm.control_program_option_date_between')"
                                            />
                                            <v-radio
                                                value="isDateNearOrEqualTo"
                                                :label="$t('alarm.control_program_option_is_date_near_or_equal_to')"
                                            />
                                        </div>
                                        
                                        <div v-if="type.fieldType === 'STRING'">
                                            <v-radio
                                                value="stringCompareSize"
                                                :label="$t('alarm.control_program_option_string_compare_size')"
                                            />
                                            <v-radio
                                                value="stringContain"
                                                :label="$t('alarm.control_program_option_string_contain')"
                                            />
                                            <v-radio
                                                value="stringNotContain"
                                                :label="$t('alarm.control_program_option_string_not_contain')"
                                            />
                                        </div>
                                        <div v-if="type.fieldType === 'NUMBER'">
                                            <v-radio
                                                value="numberCompareNumber"
                                                :label="$t('alarm.control_program_option_number_compare_number')"
                                            />
                                            <v-radio
                                                value="numberBetweenTwoNumber"
                                                :label="$t('alarm.control_program_option_number_between_two_number')"
                                            />
                                        </div>
                                        <div v-if="type.fieldType === 'BOOLEAN'"></div>
                                        <div v-if="type.fieldType === 'LIST'"></div>
                                        <div v-if="type.fieldType === 'ENUM'"></div>
                                        <div v-if="type.fieldType === 'COLLECTION'"></div>
                                        <div v-if="type.fieldType === 'STATIC'">
                                        
                                        </div>
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="7">
                                    <v-row dense>
                                        <v-col cols="12"
                                               v-if="['DATE', 'DATETIME', 'TIME', 'TIMESTAMP'].includes(type.fieldType)">
                                            <v-row
                                                v-if="['equal', 'notEqual', 'dateBetween'].includes(controlProgram.option)"
                                                dense
                                            >
                                                <v-col cols="12">
                                                    <strong class='red--text'>{{ type.label }}</strong>
                                                </v-col>
                                                <v-col cols="12">
                                                    <w-label :label="$t('common.from')" required/>
                                                    <w-date-picker
                                                        v-model="controlProgram.firstDate"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col
                                                    v-if="['DATETIME', 'TIME', 'TIMESTAMP'].includes(type.fieldType)"
                                                    cols="12"
                                                >
                                                    <v-row dense>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="controlProgram.activateFirstTime"
                                                                :label="$t('alarm.activate_time')"
                                                                :readonly="readonly"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                        <v-col
                                                            v-if="controlProgram.activateFirstTime"
                                                            cols="12"
                                                        >
                                                            <w-time-picker
                                                                v-model="controlProgram.firstTime"
                                                                :readonly="readonly"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                                <v-col
                                                    v-if="['dateBetween'].includes(controlProgram.option)"
                                                    cols="12"
                                                >
                                                    <v-row dense>
                                                        <v-col cols="12">
                                                            <w-label :label="$t('common.to')" required/>
                                                            <w-date-picker
                                                                v-model="controlProgram.secondDate"
                                                                :readonly="readonly"
                                                                :rules="[mxRules.required]"
                                                            />
                                                        </v-col>
                                                        <v-col
                                                            v-if="['DATETIME', 'TIME', 'TIMESTAMP'].includes(type.fieldType)"
                                                            cols="12"
                                                        >
                                                            <v-row dense>
                                                                <v-col cols="12">
                                                                    <v-checkbox
                                                                        v-model="controlProgram.activateSecondTime"
                                                                        :label="$t('alarm.activate_time')"
                                                                        :readonly="readonly"
                                                                        hide-details
                                                                    />
                                                                </v-col>
                                                                <v-col
                                                                    v-if="controlProgram.activateSecondTime"
                                                                    cols="12"
                                                                >
                                                                    <w-time-picker
                                                                        v-model="controlProgram.secondTime"
                                                                        :readonly="readonly"
                                                                    />
                                                                </v-col>
                                                            </v-row>
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                            </v-row>
                                            <v-row
                                                v-if="controlProgram.option === 'isDateNearOrEqualTo'"
                                                dense
                                            >
                                                <v-col cols="12">
                                                    <w-enum-select
                                                        v-model="controlProgram.operator"
                                                        :items="simpleControlOperators"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col cols="12">
                                                    <w-text-field
                                                        v-model="controlProgram.days"
                                                        :readonly="readonly"
                                                        :prefix="`${$t('alarm.date_set')}: `"
                                                        :suffix="$t('common.days')"
                                                        type="number"
                                                        step="1"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col cols="12">
                                                    <v-radio-group
                                                        v-model="controlProgram.dateCompareOption"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                    >
                                                        <v-radio
                                                            value="FULL"
                                                            :label="$t('alarm.control_program_date_compare_option_FULL')"
                                                        />
                                                        <v-radio
                                                            value="MONTH"
                                                            :label="$t('alarm.control_program_date_compare_option_MONTH')"
                                                        />
                                                        <v-radio
                                                            value="DAY"
                                                            :label="$t('alarm.control_program_date_compare_option_DAY')"
                                                        />
                                                    </v-radio-group>
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                        <v-col
                                            v-if="controlProgram.option === 'stringCompareSize' && type.fieldType === 'STRING'"
                                            cols="12"
                                        >
                                            <v-row dense>
                                                <v-col cols="12">
                                                    <strong class='red--text'>{{ type.label }}</strong>
                                                </v-col>
                                                <v-col cols="12">
                                                    <w-enum-select
                                                        v-model="controlProgram.operator"
                                                        :items="simpleControlOperators"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col cols="12">
                                                    <w-text-field
                                                        v-model="controlProgram.size"
                                                        :readonly="readonly"
                                                        :suffix="$t('alarm.character')"
                                                        type="number"
                                                        step="1"
                                                        :rules="[mxRules.required]"
                                                    ></w-text-field>
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                        <v-col
                                            v-if="['equal', 'notEqual', 'stringContain', 'stringNotContain'].includes(controlProgram.option) && type.fieldType === 'STRING'"
                                            cols="12"
                                        >
                                            <v-row dense>
                                                <v-col cols="12">
                                                    <strong class='red--text'>{{ type.label }}</strong>
                                                </v-col>
                                                <v-col cols="12">
                                                    <w-label :label="$t('common.value')" required/>
                                                    <w-text-field
                                                        v-model="controlProgram.anotherString"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                        hide-details="auto"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                        <v-col
                                            v-if="['equal', 'notEqual'].includes(controlProgram.option) && type.fieldType === 'BOOLEAN'"
                                            cols="12"
                                        >
                                            <v-radio-group
                                                row
                                                v-model="controlProgram.booleanCheck"
                                                :readonly="readonly"
                                                hide-details="auto"
                                            >
                                                <v-radio
                                                    :value="true"
                                                    color="teal"
                                                    :label="$t('common.true')"
                                                />
                                                <v-radio
                                                    :value="false"
                                                    color="red"
                                                    :label="$t('common.false')"
                                                />
                                            </v-radio-group>
                                        </v-col>
                                        <v-col
                                            v-if="type.fieldType === 'NUMBER' && ['equal', 'notEqual', 'numberCompareNumber', 'numberBetweenTwoNumber'].includes(controlProgram.option)"
                                            cols="12"
                                        >
                                            <v-row dense>
                                                <v-col cols="12">
                                                    <strong class='red--text'>{{ type.label }}</strong>
                                                </v-col>
                                                <v-col
                                                    v-if="['numberCompareNumber', 'numberBetweenTwoNumber'].includes(controlProgram.option)"
                                                    cols="12"
                                                >
                                                    <w-enum-select
                                                        v-model="controlProgram.operator"
                                                        :items="simpleControlOperators"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col cols="12">
                                                    <w-text-field
                                                        v-model="controlProgram.firstNumber"
                                                        :readonly="readonly"
                                                        type="number"
                                                        step="1"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col
                                                    v-if="['numberBetweenTwoNumber'].includes(controlProgram.option)"
                                                    cols="12"
                                                >
                                                    <w-enum-select
                                                        v-model="controlProgram.secondOperator"
                                                        :items="simpleControlOperators"
                                                        :readonly="readonly"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                                <v-col
                                                    v-if="['numberBetweenTwoNumber'].includes(controlProgram.option)"
                                                    cols="12"
                                                >
                                                    <w-text-field
                                                        v-model="controlProgram.secondNumber"
                                                        :readonly="readonly"
                                                        type="number"
                                                        step="1"
                                                        :rules="[mxRules.required]"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                        <v-col
                                            v-if="type.fieldType === 'LIST' && ['equal', 'notEqual'].includes(controlProgram.option)"
                                            cols="12"
                                        >
                                            <v-col cols="12">
                                                <v-row dense v-if="['countryStates'].includes(type.listName)">
                                                    <v-col cols="12">
                                                        <w-label :label="$t('common.country_state')" required/>
                                                        <w-state-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                </v-row>
                                                <v-row v-if="['countryCities'].includes(type.listName)">
                                                    <v-col cols="12">
                                                       <w-label :label="$t('common.country_state')" required/>
                                                        <w-state-autocomplete
                                                            v-model="controlProgram.parentListId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col cols="12">
                                                        <w-label :label="$t('common.country_city')" required />
                                                        <w-city-autocomplete
                                                            v-model="controlProgram.listId"
                                                            item-value="id"
                                                            :state-id="controlProgram.parentListId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                </v-row>
                                                <v-row dense>
                                                    <v-col
                                                        v-if="type.listName === 'contratTemplates'"
                                                        cols="12"
                                                    >
                                                        <w-label :label="$t('module.template')" required/>
                                                        <str-contrat-template-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col
                                                        v-if="type.listName === 'workers'"
                                                        cols="12"
                                                    >
                                                        <strong>{{ $t('module.worker') }}</strong>
                                                        <str-worker-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col
                                                        v-if="type.listName === 'users'"
                                                        cols="12"
                                                    >
                                                        <w-label :label="$t('module.user')" required/>
                                                        <str-user-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col
                                                        v-if="['occupations', 'banks', 'bankAgencies'].includes(type.listName)"
                                                        cols="12"
                                                    >
                                                        <str-worker-def-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :resource="type.listName"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col
                                                        v-if="['organizationalCharts'].includes(type.listName)"
                                                        cols="12"
                                                    >
                                                        <organizational-chart-field
                                                            v-model="controlProgram.listId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col
                                                        v-if="[ 'cnas'].includes(type.listName)"
                                                        cols="12"
                                                    >
                                                        <str-cnas-agcency-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col
                                                        v-if="['cnasDeclarations', 'mutualCompanies', 'leavingCauses', 'companies', 'workplaces',  'states', 'familySituations', 'contratCharacteristics'].includes(type.listName)"
                                                        cols="12"
                                                    >
                                                        <w-data-select-autocomplete
                                                            v-model="controlProgram.listId"
                                                            :type="getDataSelectTag(type.listName)"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </v-col>
                                        </v-col>
                                        <v-col
                                            v-if="type.fieldType === 'ENUM' && ['equal', 'notEqual'].includes(controlProgram.option)"
                                            cols="12"
                                        >
                                            <w-enum-select
                                                v-model="controlProgram.enumValue"
                                                :items="getEnumList(type.listName)"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>

</style>